import { QuestionForMain } from "@/components/features/_layouts/island/main/question";
import type { SiteControllerSettingBaseFormModel } from "@/components/features/settings/site-controller/model/base/type";
import type { SiteControllerSettingTllFormModel } from "@/components/features/settings/site-controller/model/tl-lincoln/type";
import type { SiteControllerSettingTLLincolnFormModelMethodsType } from "@/components/features/settings/site-controller/model/tl-lincoln/use-case/use-create-model/methods";

import { SelectField } from "@/components/ui/form/fields/select-field";

import { useTlLincolnSetChillnnRoomID } from "./hooks";

type Props = {
	rooms: SiteControllerSettingTllFormModel["ViewModel"]["tll"]["rooms"]; //
	onSetChillnnRoomID: SiteControllerSettingTLLincolnFormModelMethodsType["siteControllerSettingTLLFormModelMethods"]["onSetChillnnRoomID"];

	roomKVList: SiteControllerSettingBaseFormModel["QueryData"]["roomKVList"];
};

const SectionWrapper = ({ children, title }: { children: React.ReactNode; title: string }) => {
	return (
		<div className={"col-span-1 space-y-2"}>
			<p className="text-sm font-semibold underline underline-offset-4">{title}</p>
			{children}
		</div>
	);
};

const SupplementaryTextWrapper = ({ children }: { children: React.ReactNode }) => {
	return <div className="rounded bg-zinc-200 px-4 py-2 text-sm">{children}</div>;
};

export const SettingsSiteControllerTLLRoomMapping = ({
	rooms,
	onSetChillnnRoomID: _onSetChillnnRoomID,
	roomKVList,
}: Props) => {
	const { chillnnRoomIDOptions, onSetChillnnRoomID } = useTlLincolnSetChillnnRoomID({
		rooms,
		onSetChillnnRoomID: _onSetChillnnRoomID,
		roomKVList,
	});

	return (
		<>
			{Boolean(rooms.value.length) && (
				<QuestionForMain
					required={true}
					title={"TL-リンカーンの部屋とCHILLNNの部屋の紐づけ"}
					inputChildren={
						<>
							{rooms.value.map((room, idx) => {
								const shouldShowRelationalRoomTypeID = !!room.chillnnRoomID?.value;

								return (
									<div
										className="rounded border border-zinc-200 px-4 py-6"
										key={room.tllRoomID.value + idx}
									>
										<div className="grid grid-cols-2 gap-x-4 gap-y-6">
											<div className="col-span-2 mb-2 border-b border-b-zinc-200 pb-6">
												<div className="grid grid-cols-2 gap-x-4">
													<SectionWrapper title="TL-リンカーンの部屋">
														<p>{room.tllRoomTypeName.value}</p>
													</SectionWrapper>
													<SectionWrapper title="CHILLNNの部屋">
														<SelectField
															value={room.chillnnRoomID?.value || "null"}
															options={chillnnRoomIDOptions(
																room.chillnnRoomID?.value ?? undefined,
															)}
															handleValueChange={(input) =>
																onSetChillnnRoomID({
																	chillnnRoomID: input,
																	tllRoomID: room.tllRoomID.value,
																})
															}
														/>
													</SectionWrapper>
												</div>
											</div>

											<SectionWrapper title="TL-リンカーンプランでの販売可否">
												<p>{room.inSale.value ? "販売中" : "販売不可"}</p>
											</SectionWrapper>

											<SectionWrapper title="CHILLNNプランでの販売可否">
												<p>{room.inSaleForChillnnPlan?.value ? "販売中" : "販売不可"}</p>
												{!room.inSaleForChillnnPlan?.value && (
													<SupplementaryTextWrapper>
														TL-リンカーンにて、販売先プランでの販売が許可されていません。
													</SupplementaryTextWrapper>
												)}
											</SectionWrapper>

											<SectionWrapper title="利用可能人数">
												<p>
													{room.roomMinimumNum?.value} ~ {room.roomMaximumNum?.value}人
												</p>
											</SectionWrapper>

											{shouldShowRelationalRoomTypeID && (
												<SectionWrapper title="関連室タイプコード">
													<p>{room.relationalRoomTypeID?.value || "未設定"}</p>

													{!room.relationalRoomTypeID?.value && (
														<SupplementaryTextWrapper>
															<div className="flex flex-wrap gap-x-2">
																<p>TL-リンカーンで</p>
																<p>「{room.chillnnRoomID?.value?.slice(-12)}」を</p>
																<p>
																	「{room.tllRoomTypeName.value}
																	」の販売先室タイプコードとして登録してください。
																</p>
															</div>
														</SupplementaryTextWrapper>
													)}
												</SectionWrapper>
											)}
										</div>
									</div>
								);
							})}
						</>
					}
				/>
			)}{" "}
		</>
	);
};

SettingsSiteControllerTLLRoomMapping.displayName = "SettingsSiteControllerTLLRoomMapping";
